import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import Card from "../../components/Card";
import { 
  ArrowLeft, 
  Home, 
  Settings, 
  Save,
  Globe,
  Mail,
  Shield,
  Database,
  Bell,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Info
} from "lucide-react";

interface SystemConfig {
  siteName: string;
  siteUrl: string;
  supportEmail: string;
  maintenanceMode: boolean;
  allowRegistration: boolean;
  requireEmailVerification: boolean;
  maxStoragePerUser: number;
  defaultUserRole: string;
  enableNotifications: boolean;
  paymentGateway: string;
  stripePublicKey: string;
  stripeSecretKey: string;
  cloudflareAccountId: string;
  cloudflareR2AccessKey: string;
  cloudflareR2SecretKey: string;
  cloudflareR2BucketName: string;
}

export default function SystemSettings() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<SystemConfig>({
    siteName: "EduMi",
    siteUrl: "https://coursedee.web.app",
    supportEmail: "support@edumi.co",
    maintenanceMode: false,
    allowRegistration: true,
    requireEmailVerification: true,
    maxStoragePerUser: 10,
    defaultUserRole: "student",
    enableNotifications: true,
    paymentGateway: "stripe",
    stripePublicKey: "",
    stripeSecretKey: "",
    cloudflareAccountId: "",
    cloudflareR2AccessKey: "",
    cloudflareR2SecretKey: "",
    cloudflareR2BucketName: "edumi-storage"
  });
  const [saveStatus, setSaveStatus] = useState<"idle" | "success" | "error">("idle");
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    if (user?.email !== "viitamint@gmail.com") {
      navigate("/");
      return;
    }
    loadConfig();
  }, [user, navigate]);

  const loadConfig = async () => {
    try {
      const configDoc = await getDoc(doc(db, "system", "config"));
      if (configDoc.exists()) {
        setConfig({ ...config, ...configDoc.data() });
      }
    } catch (error) {
      console.error("Error loading config:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus("idle");
    
    try {
      await setDoc(doc(db, "system", "config"), config, { merge: true });
      setSaveStatus("success");
      setStatusMessage("บันทึกการตั้งค่าสำเร็จ");
      
      setTimeout(() => {
        setSaveStatus("idle");
        setStatusMessage("");
      }, 3000);
    } catch (error) {
      console.error("Error saving config:", error);
      setSaveStatus("error");
      setStatusMessage("เกิดข้อผิดพลาดในการบันทึก");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof SystemConfig, value: any) => {
    setConfig({ ...config, [field]: value });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-soft py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link to="/admin">
                <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                  <ArrowLeft className="w-6 h-6 text-textDark" />
                </button>
              </Link>
              <div>
                <h1 className="text-4xl font-bold text-textDark flex items-center gap-3">
                  <Settings className="w-10 h-10 text-slate-600" />
                  ตั้งค่าระบบ
                </h1>
                <p className="text-textLight">กำหนดค่าและการตั้งค่าระบบ</p>
              </div>
            </div>
            <Link to="/">
              <button className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200">
                <Home className="w-5 h-5" />
                <span>ไปหน้าแรก</span>
              </button>
            </Link>
          </div>

          {/* Save Status */}
          {saveStatus !== "idle" && (
            <div className={`p-4 rounded-xl flex items-center gap-3 mb-6 ${
              saveStatus === "success" ? "bg-green-50 border-2 border-green-200" :
              "bg-red-50 border-2 border-red-200"
            }`}>
              {saveStatus === "success" ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-600" />
              )}
              <p className={`font-semibold ${
                saveStatus === "success" ? "text-green-700" : "text-red-700"
              }`}>
                {statusMessage}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {/* General Settings */}
          <Card>
            <div className="p-6">
              <h3 className="text-xl font-bold text-textDark mb-6 flex items-center gap-2">
                <Globe className="w-6 h-6 text-blue-600" />
                การตั้งค่าทั่วไป
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-textDark mb-2">
                    ชื่อเว็บไซต์
                  </label>
                  <input
                    type="text"
                    value={config.siteName}
                    onChange={(e) => handleChange("siteName", e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-textDark mb-2">
                    URL เว็บไซต์
                  </label>
                  <input
                    type="text"
                    value={config.siteUrl}
                    onChange={(e) => handleChange("siteUrl", e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-textDark mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    อีเมลสนับสนุน
                  </label>
                  <input
                    type="email"
                    value={config.supportEmail}
                    onChange={(e) => handleChange("supportEmail", e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Security & Access Settings */}
          <Card>
            <div className="p-6">
              <h3 className="text-xl font-bold text-textDark mb-6 flex items-center gap-2">
                <Shield className="w-6 h-6 text-blue-600" />
                ความปลอดภัยและการเข้าถึง
              </h3>
              
              <div className="space-y-4">
                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-all">
                  <div>
                    <p className="font-semibold text-textDark">โหมดปรับปรุงระบบ</p>
                    <p className="text-sm text-textLight">ปิดการเข้าถึงระบบชั่วคราว</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={config.maintenanceMode}
                    onChange={(e) => handleChange("maintenanceMode", e.target.checked)}
                    className="w-6 h-6"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-all">
                  <div>
                    <p className="font-semibold text-textDark">อนุญาตการลงทะเบียน</p>
                    <p className="text-sm text-textLight">ผู้ใช้สามารถสร้างบัญชีใหม่ได้</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={config.allowRegistration}
                    onChange={(e) => handleChange("allowRegistration", e.target.checked)}
                    className="w-6 h-6"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-all">
                  <div>
                    <p className="font-semibold text-textDark">ต้องยืนยันอีเมล</p>
                    <p className="text-sm text-textLight">ผู้ใช้ต้องยืนยันอีเมลก่อนใช้งาน</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={config.requireEmailVerification}
                    onChange={(e) => handleChange("requireEmailVerification", e.target.checked)}
                    className="w-6 h-6"
                  />
                </label>

                <div>
                  <label className="block text-sm font-semibold text-textDark mb-2">
                    บทบาทเริ่มต้น
                  </label>
                  <select
                    value={config.defaultUserRole}
                    onChange={(e) => handleChange("defaultUserRole", e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  >
                    <option value="student">ผู้เรียน</option>
                    <option value="instructor">ผู้สอน</option>
                  </select>
                </div>
              </div>
            </div>
          </Card>

          {/* Storage Settings */}
          <Card>
            <div className="p-6">
              <h3 className="text-xl font-bold text-textDark mb-6 flex items-center gap-2">
                <Database className="w-6 h-6 text-green-600" />
                การจัดเก็บข้อมูล
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-textDark mb-2">
                    พื้นที่จัดเก็บสูงสุดต่อผู้ใช้ (GB)
                  </label>
                  <input
                    type="number"
                    value={config.maxStoragePerUser}
                    onChange={(e) => handleChange("maxStoragePerUser", parseInt(e.target.value))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  />
                </div>

                <div className="p-4 bg-blue-50 rounded-xl border-2 border-blue-200">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-blue-900 mb-1">Cloudflare R2 Configuration</p>
                      <p className="text-xs text-blue-700">
                        ระบบใช้ Cloudflare R2 สำหรับจัดเก็บไฟล์ กรุณาตั้งค่าใน Environment Variables
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-textDark mb-2">
                    Cloudflare Account ID
                  </label>
                  <input
                    type="text"
                    value={config.cloudflareAccountId}
                    onChange={(e) => handleChange("cloudflareAccountId", e.target.value)}
                    placeholder="ตั้งค่าใน Environment Variables"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                    disabled
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-textDark mb-2">
                    R2 Bucket Name
                  </label>
                  <input
                    type="text"
                    value={config.cloudflareR2BucketName}
                    onChange={(e) => handleChange("cloudflareR2BucketName", e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </Card>

          {/* Notification Settings */}
          <Card>
            <div className="p-6">
              <h3 className="text-xl font-bold text-textDark mb-6 flex items-center gap-2">
                <Bell className="w-6 h-6 text-pink-600" />
                การแจ้งเตือน
              </h3>
              
              <div className="space-y-4">
                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-all">
                  <div>
                    <p className="font-semibold text-textDark">เปิดใช้งานการแจ้งเตือน</p>
                    <p className="text-sm text-textLight">ส่งการแจ้งเตือนถึงผู้ใช้</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={config.enableNotifications}
                    onChange={(e) => handleChange("enableNotifications", e.target.checked)}
                    className="w-6 h-6"
                  />
                </label>
              </div>
            </div>
          </Card>

          {/* Payment Settings */}
          <Card>
            <div className="p-6">
              <h3 className="text-xl font-bold text-textDark mb-6 flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-yellow-600" />
                การชำระเงิน
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-textDark mb-2">
                    Payment Gateway
                  </label>
                  <select
                    value={config.paymentGateway}
                    onChange={(e) => handleChange("paymentGateway", e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                  >
                    <option value="stripe">Stripe</option>
                    <option value="promptpay">PromptPay</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>

                {config.paymentGateway === "stripe" && (
                  <>
                    <div>
                      <label className="block text-sm font-semibold text-textDark mb-2">
                        Stripe Public Key
                      </label>
                      <input
                        type="text"
                        value={config.stripePublicKey}
                        onChange={(e) => handleChange("stripePublicKey", e.target.value)}
                        placeholder="pk_..."
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-textDark mb-2">
                        Stripe Secret Key
                      </label>
                      <input
                        type="password"
                        value={config.stripeSecretKey}
                        onChange={(e) => handleChange("stripeSecretKey", e.target.value)}
                        placeholder="sk_..."
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary focus:outline-none"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </Card>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-gradient-primary text-white py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                กำลังบันทึก...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                บันทึกการตั้งค่า
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
